import { ViewColumnOutlined } from "@mui/icons-material";
import {
  ClickAwayListener,
  FormControlLabel,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  styled,
} from "@mui/material";
import Button from "@mui/material/Button";
import StewartCheckbox from "controls/global/stewart-checkbox";
import { orderBy } from "lodash";
import React from "react";
import {
  borderRadius,
  borderSize,
  colors,
  gaps,
  margin,
  padding,
  scrollBar,
  zIndex,
} from "theme/defaultStyle";
import theme from "theme/default";

export type MenuColumnItem<T> = {
  field: keyof T;
  name: string;
  checked: boolean;
};

type Props<T> = {
  options: MenuColumnItem<T>[];
  onChecked: (field: keyof T, checked: boolean) => void;
  onHideAll?: () => void;
  onShowAll?: () => void;
  isDisabled?: boolean;
  // Renders just the icon, no "Columns" label — for toolbars that show a
  // row of icon-only actions instead of labeled buttons.
  iconOnly?: boolean;
};

const StyledPopper = styled(Popper)({
  zIndex: zIndex.high2,
  "& .paper": {
    border: `${borderSize.xsmall} solid ${colors.grey09}`,
    marginTop: margin.small1,
    overflow: "hidden",
    "&.MuiPaper-rounded": {
      borderRadius: borderRadius.xlarge,
    },
    "& .MuiList-root": {
      maxHeight: "min(600px, calc(100vh - 300px))",
      minHeight: "100px",
      overflowY: "scroll",
      padding: padding.small0,
      margin: `${margin.small1, margin.xsmall1}`,
      "&::-webkit-scrollbar": {
        width: scrollBar.width1,
        height: scrollBar.height,
      },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: scrollBar.borderRadius1,
        backgroundColor: colors.grey20,
        border: `${borderSize.medium} solid transparent`,
        backgroundClip: "content-box",
      },
      "&::-webkit-scrollbar-track": {
        background: colors.grey21,
        boxShadow: "1px 0px 0px 0px #DEDEDE inset",
      },
      "&::-webkit-scrollbar-button": {
        display: "none",
      },
    },
    "& .MuiMenuItem-root": {
      paddingTop: padding.zero,
      paddingBottom: padding.zero,
      margin: padding.zero,
    },
    "& .MuiMenuItem-root:hover": {
      backgroundColor: colors.grey10,
      color: colors.grey08,
    },
    "& .MuiMenuItem-root.Mui-selected": {
      backgroundColor: colors.grey10,
      color: colors.grey08,
    },
    '& .MuiFormControlLabel-root': {
      marginBottom: padding.zero,
    },
    "& .buttons": {
      display: "flex",
      justifyContent: "space-between",
      paddingBottom: padding.small1,
      paddingLeft: padding.large1,
      paddingRight: padding.large1,
      paddingTop: padding.small1,
    },
  },
  "& .column-list": {
    boxShadow: "0px 1px 5px 0px #6A747805, 0px 2px 2px 0px #6A74780A, 0px 3px 10px -2px #6A74781A"
  }
});

const Arrow = styled("div")({
  "&::before, &::after": {
    border: "solid 15px transparent",
    borderBottomColor: theme.palette.background.paper,
    borderTop: 0,
    content: '""',
    display: "block",
    height: 0,
    left: "-10px",
    overflow: "hidden",
    position: "absolute",
    top: "-5px",
    width: 0,
    zIndex: "2",
  },
  "&::before": {
    borderBottomColor: colors.grey09,
    top: "-6.5px",
    zIndex: 1,
  },
});

const TextStyles = styled('span')({
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: gaps.small1,
});

export default function ColumnsConfig<T>({
  options,
  onChecked,
  onHideAll,
  onShowAll,
  isDisabled,
  iconOnly,
}: Props<T>) {
  const [columns, setColumns] = React.useState<MenuColumnItem<T>[] | undefined>();
  const [colMenuOpen, setColMenuOpen] = React.useState<boolean>(false);
  const anchorRef = React.useRef<any>();
  const [arrowRef, setArrowRef] = React.useState<HTMLElement | null>(null);
  const [selectAll, setSelectAll] = React.useState<boolean>(false);
  const [partialSelect, setPartialSelect] = React.useState<boolean>(false);
  const [checkedCount, setCheckedCount] = React.useState<number>(0);

  React.useEffect(() => {
    const sortedColumns = orderBy(options, "name");
    setColumns(sortedColumns);
    setInitialSelectAllValue(sortedColumns);
    setCheckedCount(sortedColumns.filter(x => x.checked).length);
  }, [options]);

  const handleCheckBoxChange = () => {
    if (selectAll) {
      onHideAll && onHideAll(); 
      setSelectAll(false);
      setPartialSelect(false);
      setCheckedCount(0);
    } else {
      onShowAll && onShowAll();
      setSelectAll(true);
      setPartialSelect(false);
      setCheckedCount(columns?.length ?? 0);
    }
  };

  const setInitialSelectAllValue = (sortedColumns: MenuColumnItem<T>[]) => {
    const checkedCount = sortedColumns.filter(x => x.checked).length;
    if (checkedCount === 0) {
      setSelectAll(false);
    } else if (checkedCount === sortedColumns.length) {
      setSelectAll(true);
    } else {
      setPartialSelect(true);
    }
  };

  const setSelectionState = (field: keyof T, checked: boolean) => {
    if (columns) {
      const updatedColumns = columns.map(x => {
        if (x.field === field) {
          x.checked = !checked;
        }
        return x;
      });
      const checkedCount = updatedColumns.filter(z => z.checked).length;
      setCheckedCount(checkedCount);
      const allUnchecked = checkedCount === 0;
      setSelectAll(!allUnchecked && checkedCount === updatedColumns.length);
      setPartialSelect(!allUnchecked && checkedCount < updatedColumns.length);
    }
  };

  if (!columns) return null;
  return (
    <>
      <Button
        onClick={() => setColMenuOpen(!colMenuOpen)}
        data-test="myFilesColumns"
        ref={anchorRef}
        disabled={isDisabled}
      >
        {iconOnly ? (
          <ViewColumnOutlined />
        ) : (
          <TextStyles>
            Columns
            <ViewColumnOutlined />
          </TextStyles>
        )}
      </Button>
      <StyledPopper
        open={colMenuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        disablePortal
        placement="bottom-end"
        modifiers={[
          {
            name: "arrow",
            enabled: true,
            options: {
              element: arrowRef,
            },
          },
        ]}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <>
              <Arrow ref={setArrowRef} />
              <Paper className={"paper"} elevation={3}>
                <ClickAwayListener onClickAway={() => setColMenuOpen(false)}>
                  <MenuList
                    autoFocusItem={colMenuOpen}
                    id="menu-list-grow"
                    className="column-list"
                  >
                    <MenuItem style={{ paddingLeft: "6px" }}>
                      <FormControlLabel
                        control={
                          <StewartCheckbox
                            onChange={handleCheckBoxChange}
                            name="select-all"
                            checked={selectAll}
                            indeterminate={partialSelect}
                          />
                        }
                        label="All Columns"
                      />
                    </MenuItem>
                    {columns.map(({ field, name, checked }, index) => (
                      <MenuItem key={String(field)}>
                        <FormControlLabel
                          control={
                            <StewartCheckbox
                              checked={checked}
                              onChange={() => {
                                onChecked(field, checked);
                                setSelectionState(field, checked);
                              }}
                              name={String(field)}
                              disabled={checked && checkedCount === 1}
                            />
                          }
                          label={name}
                        />
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </>
          </Grow>
        )}
      </StyledPopper>
    </>
  );
}
